import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Caixa from './Caixa';
import Text from './Text';
import TroncCalculations from './TroncCalculations';
import { fitPrintBounds, getEditorPrintPages, measurePrintBounds } from './editorPrintLayout';
import { getSubdomain } from '../../utils/utils';
import SETTINGS from '../../SETTINGS';
import './PrintableEditor.css';

const COLLA = getSubdomain();

function PrintableEditorPage({ page, json, mode, addons, title, isBundle }) {
    const viewportRef = useRef(null);
    const sceneRef = useRef(null);

    useLayoutEffect(() => {
        let disposed = false;
        const fit = () => {
            if (disposed) return;
            const scene = sceneRef.current;
            const viewport = viewportRef.current;
            scene.style.transform = 'none';
            const { scale, x, y } = fitPrintBounds(measurePrintBounds(scene), viewport.clientWidth, viewport.clientHeight);
            scene.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
        };
        fit();
        // Re-measure synchronously for native keyboard/menu printing, and when
        // fonts finish loading or a long header changes the available space.
        window.addEventListener('beforeprint', fit);
        document.fonts?.ready.then(fit);
        document.fonts?.addEventListener('loadingdone', fit);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
        observer?.observe(viewportRef.current);
        return () => {
            disposed = true;
            window.removeEventListener('beforeprint', fit);
            document.fonts?.removeEventListener('loadingdone', fit);
            observer?.disconnect();
        };
    }, [page, json, mode, addons, title, isBundle]);

    const incoming = useMemo(() => {
        const result = {};
        Object.entries(json).forEach(([id, item]) => {
            if (item?.type !== 'caixa') return;
            [['escala_a', 'escaladors'], ['trepitja_a', 'trepitjadors']].forEach(([field, name]) => {
                if (!Array.isArray(item[field])) return;
                item[field].forEach(target => {
                    if (!result[target]) result[target] = { escaladors: [], trepitjadors: [] };
                    result[target][name].push(id);
                });
            });
        });
        return result;
    }, [json]);

    return (
        <section className="editor-print-page">
            <header className="editor-print-header">
                <img src={`/logos/${COLLA}.png`} alt={SETTINGS(COLLA).fullname} />
                <div className="editor-print-title">
                    <h1>{title}</h1>
                    <div>{SETTINGS(COLLA).fullname}</div>
                </div>
                <h2>{page.name}</h2>
            </header>
            <div className="editor-print-viewport" ref={viewportRef}>
                <div className="editor-print-scene" ref={sceneRef}>
                    {page.entries.map(([id, item]) => item.type === 'text' ? (
                        <Text key={id} printable textValue={item.value} boxProps={item.box} textProps={item.text} />
                    ) : (
                        <Caixa
                            key={id}
                            {...item}
                            {...incoming[id]}
                            id={id}
                            boxProps={item.box}
                            textProps={item.text}
                            fontSize={json.settings?.fontSize || 14}
                            mode={mode}
                            opacity={1}
                            disabled={'clone' in item}
                            isEscaleta={item.escala_a?.length > 0}
                            isTrepitjador={item.trepitja_a?.length > 0}
                            printable
                            readonly
                        />
                    ))}
                    {isBundle && page.name.toLowerCase() === 'tronc' && (
                        <TroncCalculations json={json} pestanya={page.name} mode={mode} addons={addons} opacity={1} printable readonly />
                    )}
                </div>
            </div>
        </section>
    );
}

function PrintableEditor({ json, tabs, mode, addons, bundleInfo, selectedBundle, selectedCastellPart, plantillaDefaultName }) {
    const pages = useMemo(() => getEditorPrintPages(json, tabs), [json, tabs]);
    const title = bundleInfo?.nom || selectedCastellPart || plantillaDefaultName || selectedBundle || 'Plantilla sense nom';

    useEffect(() => {
        if (!pages.length) return;
        document.body.classList.add('editor-print-active');
        return () => document.body.classList.remove('editor-print-active');
    }, [pages.length]);

    if (!pages.length) return null;
    // A body portal avoids the app's flex/overflow containers and also lets us
    // hide modal portals and global banners without affecting other pages.
    return createPortal(
        <div className="editor-print-root" aria-hidden="true">
            {/* Page rules cannot be scoped with a selector. Mount this after the
                global board styles, and remove it when leaving the editor. */}
            <style media="print">{'@page { size: A4 landscape; margin: 0; }'}</style>
            {pages.map(page => <PrintableEditorPage key={page.name} page={page} json={json} mode={mode}
                addons={addons} title={title} isBundle={Boolean(selectedBundle)} />)}
        </div>,
        document.body
    );
}

export default React.memo(PrintableEditor);
